import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { IMG } from '../lib/images';
import { FEATURED_BLOG_POSTS } from '../data/editorialContent';
import { useTheme } from '../contexts/ThemeContext';

const mergePostsBySlug = (posts = []) => {
  const bySlug = new Map();
  [...FEATURED_BLOG_POSTS, ...posts].forEach((post) => {
    if (!post?.slug) return;
    bySlug.set(post.slug, {
      ...post,
      cover_image: post.cover_image || post.image || '',
    });
  });
  return Array.from(bySlug.values()).sort(
    (left, right) => new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime()
  );
};

const BlogPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await api.get('/public/blog');
      setPosts(mergePostsBySlug(response.data || []));
    } catch (error) {
      console.error('Failed to load posts:', error);
      setPosts(mergePostsBySlug());
    } finally {
      setLoading(false);
    }
  };

  const FALLBACK_IMAGES = [
    IMG.card.aiDashboard, IMG.card.dataDriven, IMG.card.meetingRoom,
    IMG.card.graphGrowth, IMG.card.focusWork, IMG.card.codeScreen,
  ];

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-devices" data-testid="blog-page">
      {/* Hero */}
      <section
        className="relative overflow-hidden resource-hero"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.blog})`
            : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.94)), url(${IMG.hero.blog})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-16 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 bg-black/40 border border-white/20 rounded-full">
              <BookOpen size={14} className="text-cyber-yellow" />
              <span className="text-xs text-text-secondary uppercase tracking-widest font-mono">Editorial</span>
            </div>
            <h1 
              className="text-4xl sm:text-5xl font-black text-white mb-4"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              Blog
            </h1>
            <p className="text-lg text-text-secondary max-w-3xl">
              Strategy, implementation notes, and grounded field guidance for teams trying to apply AI without adding tool chaos.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-center text-text-secondary">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow"></div>
            <p className="mt-4">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-text-secondary">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="terminal-grid card-hover overflow-hidden"
              >
                <img 
                  src={post.cover_image || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]} 
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <p className="text-sm text-text-secondary mb-4">
                    {post.excerpt}
                  </p>
                )}
                <div className="flex items-center gap-4 text-xs text-text-muted mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{new Date(post.created_at || post.updated_at || Date.now()).toLocaleDateString()}</span>
                  </div>
                  {post.author && (
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>{post.author}</span>
                    </div>
                  )}
                  {post.views > 0 && (
                    <span>{post.views} views</span>
                  )}
                </div>
                <Link
                  to={`/blog/${post.slug}`}
                  className="text-cyber-yellow hover:underline inline-flex items-center gap-2"
                >
                  Read More
                  <ArrowRight size={16} />
                </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default BlogPage;
